import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Ersteinrichtung } from './Ersteinrichtung';
import { GeraeteAktivierung } from './geraeteaktivierung';
import {
  ermittleGeraeteInfo,
  waehleSicherenSpeicher,
  type SichererSpeicher,
} from './sicherer-speicher';
import { HttpAktivierung, type AktivierungsSchnittstelle, type GeraeteInfo } from './vertrag';
import {
  ALT_KASSENSCHLUESSEL,
  ALT_KASSEN_ID,
  ermittleEinrichtung,
  type Einrichtung,
} from './zugang';

/**
 * App-Einstieg mit Geräteaktivierung V2 (Stufe 1, Empfehlung O-1): entscheidet beim Start, ob die
 * Kasse direkt startet oder die Ersteinrichtung zeigt.
 *
 * - Bisherige Einrichtung (Kassen-ID + Kassenschlüssel in localStorage) → App wie bisher; sie wird
 *   nie überschrieben.
 * - V2-Zugang im sicheren Speicher → in localStorage spiegeln (App, Küchenmonitor und
 *   Kundenanzeige lesen dort) → App. Automatisches Wiederanmelden nach jedem Neustart.
 * - Kein Zugang → Ersteinrichtung (EA-1 …); „Erweiterte Einrichtung“ → App mit der bisherigen
 *   Einrichtung. Ohne sicheren Speicher (z. B. http im LAN) gleich die App.
 * - Zugang unlesbar → einmal nach kurzer Pause erneut lesen (vorübergehender Keystore-Fehler),
 *   dann Ersteinrichtung.
 */
export interface KassenStartProps {
  /** Die bisherige Kassen-App; wird erst nach der Entscheidung eingehängt. */
  app: () => ReactNode;
  basisUrl: string;
  /** Für Tests; Standard: `window`-Umgebung. */
  speicher?: SichererSpeicher | null;
  schnittstelle?: AktivierungsSchnittstelle;
  ablage?: Pick<Storage, 'getItem' | 'setItem'>;
  bilanxGeraet?: unknown;
  online?: () => boolean;
  wiederholungMs?: number;
  /** „Erneut versuchen“ im Speicherfehler; Standard: Seite neu laden. */
  neuLaden?: () => void;
}

type Phase =
  | { art: 'pruefe' }
  | { art: 'app' }
  /** Browser-Speicher nicht les- oder beschreibbar: nie eine App ohne Zugangsdaten starten. */
  | { art: 'speicherfehler' }
  | { art: 'ersteinrichtung'; speicher: SichererSpeicher };

/** Spiegel schreiben und zurücklesen; false = App fände ihre Zugangsdaten nicht. */
function spiegle(
  ablage: Pick<Storage, 'getItem' | 'setItem'>,
  kassenId: string,
  nachweis: string,
): boolean {
  try {
    ablage.setItem(ALT_KASSEN_ID, kassenId);
    ablage.setItem(ALT_KASSENSCHLUESSEL, nachweis);
    return (
      ablage.getItem(ALT_KASSEN_ID) === kassenId &&
      ablage.getItem(ALT_KASSENSCHLUESSEL) === nachweis
    );
  } catch {
    return false;
  }
}

function hatSpiegel(ablage: Pick<Storage, 'getItem'>): boolean {
  try {
    return (
      (ablage.getItem(ALT_KASSEN_ID) ?? '') !== '' &&
      (ablage.getItem(ALT_KASSENSCHLUESSEL) ?? '') !== ''
    );
  } catch {
    return false;
  }
}

function webGeraet(): GeraeteInfo {
  const n = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return {
    plattform: n.includes('Windows') ? 'windows' : n.includes('Android') ? 'android' : 'web',
  };
}

export function KassenStart(props: KassenStartProps) {
  const ablage = props.ablage ?? localStorage;
  const bilanxGeraet =
    'bilanxGeraet' in props
      ? props.bilanxGeraet
      : (window as unknown as { BilanxGeraet?: unknown }).BilanxGeraet;
  const speicher = useMemo<SichererSpeicher | null>(
    () =>
      props.speicher !== undefined
        ? props.speicher
        : waehleSicherenSpeicher({
            BilanxGeraet: bilanxGeraet,
            indexedDB: window.indexedDB,
            crypto: window.crypto,
            isSecureContext: window.isSecureContext,
          }),
    [],
  );
  const [phase, setPhase] = useState<Phase>({ art: 'pruefe' });
  const [geraet, setGeraet] = useState<GeraeteInfo>(webGeraet);

  useEffect(() => {
    const lauf = { aktiv: true };
    const entscheide = async (): Promise<Phase> => {
      // Bisherige Einrichtung zuerst: sie wird nie überschrieben oder verdrängt
      if (hatSpiegel(ablage)) {
        return { art: 'app' };
      }
      let e: Einrichtung = await ermittleEinrichtung(speicher, ablage);
      if (e.art === 'unlesbar') {
        await new Promise((r) => setTimeout(r, props.wiederholungMs ?? 1500));
        e = await ermittleEinrichtung(speicher, ablage);
      }
      if (e.art === 'v2') {
        // Nur füllen (eine bisherige Einrichtung hat oben schon die App gestartet); die App liest
        // localStorage – ohne lesbaren Spiegel nicht starten
        return spiegle(ablage, e.zugang.kassenId, e.zugang.nachweis)
          ? { art: 'app' }
          : { art: 'speicherfehler' };
      }
      if (e.art === 'alt' || speicher === null) {
        return { art: 'app' };
      }
      return { art: 'ersteinrichtung', speicher };
    };
    void entscheide()
      .catch((): Phase => ({ art: 'speicherfehler' }))
      .then((p) => {
        if (lauf.aktiv) {
          setPhase(p);
        }
      });
    void ermittleGeraeteInfo(bilanxGeraet, webGeraet()).then((g) => {
      if (lauf.aktiv) {
        setGeraet(g);
      }
    });
    return () => {
      lauf.aktiv = false;
    };
  }, []);

  if (phase.art === 'pruefe') {
    return null;
  }
  if (phase.art === 'app') {
    return <>{props.app()}</>;
  }
  if (phase.art === 'speicherfehler') {
    return (
      <Startfehler
        onNeu={
          props.neuLaden ??
          (() => {
            location.reload();
          })
        }
        onErweitert={() => {
          setPhase({ art: 'app' });
        }}
      />
    );
  }
  return (
    <ErsteinrichtungMitAutomat
      speicher={phase.speicher}
      geraet={geraet}
      schnittstelle={props.schnittstelle ?? new HttpAktivierung({ basisUrl: props.basisUrl })}
      spiegel={ablage}
      bilanxGeraet={bilanxGeraet}
      online={props.online ?? (() => navigator.onLine)}
      onApp={() => {
        // Nach der Aktivierung hat der Automat gespiegelt; fehlt der Spiegel, nicht ohne Zugang starten
        setPhase(hatSpiegel(ablage) ? { art: 'app' } : { art: 'speicherfehler' });
      }}
      onErweitert={() => {
        setPhase({ art: 'app' });
      }}
    />
  );
}

function ErsteinrichtungMitAutomat(props: {
  speicher: SichererSpeicher;
  geraet: GeraeteInfo;
  schnittstelle: AktivierungsSchnittstelle;
  spiegel: Pick<Storage, 'setItem'>;
  bilanxGeraet: unknown;
  online: () => boolean;
  onApp: () => void;
  onErweitert: () => void;
}) {
  // Geräteangaben der Hülle können nach dem ersten Zeichnen eintreffen
  const geraetRef = useRef(props.geraet);
  geraetRef.current = props.geraet;
  const aktivierung = useMemo(
    () =>
      new GeraeteAktivierung({
        schnittstelle: props.schnittstelle,
        speicher: props.speicher,
        geraet: () => geraetRef.current,
        // Stufe 1 (O-1): Spiegel in localStorage, bis App/Küchenmonitor/Kundenanzeige den
        // ZugangsHalter nutzen (Änderungswunsch AV3-4)
        spiegel: props.spiegel,
        online: props.online,
      }),
    [],
  );
  const [online, setOnline] = useState(props.online);
  useEffect(() => {
    void aktivierung.initialisiere().catch(() => undefined);
    const an = (): void => {
      setOnline(true);
      aktivierung.beiWiederverbindung();
    };
    const aus = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', an);
    window.addEventListener('offline', aus);
    return () => {
      window.removeEventListener('online', an);
      window.removeEventListener('offline', aus);
      aktivierung.beende();
    };
  }, [aktivierung]);
  return (
    <Ersteinrichtung
      aktivierung={aktivierung}
      bilanxGeraet={props.bilanxGeraet}
      online={online}
      onErweitert={() => {
        aktivierung.beende();
        props.onErweitert();
      }}
      onWeiter={props.onApp}
    />
  );
}

function Startfehler(props: { onNeu: () => void; onErweitert: () => void }) {
  return (
    <main className="ea-flaeche">
      <section className="ea-karte" role="alert">
        <span className="ea-symbol ea-symbol-gefahr" aria-hidden="true">
          !
        </span>
        <h1>Gerätespeicher nicht verfügbar</h1>
        <p className="ea-text">
          Die Kasse kann ihre Zugangsdaten auf diesem Gerät gerade nicht lesen oder speichern. Bitte
          erneut versuchen; bleibt der Fehler, die Website-Daten bzw. den Speicher des Browsers
          prüfen oder den Support verständigen.
        </p>
        <div className="ea-tasten">
          <button type="button" className="taste zweit" onClick={props.onErweitert}>
            Erweiterte Einrichtung
          </button>
          <button type="button" className="taste" onClick={props.onNeu}>
            Erneut versuchen
          </button>
        </div>
      </section>
    </main>
  );
}
